import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  InputAdornment,
  Tabs,
  Tab,
  Badge,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search,
  FilterList,
  Event,
  Person,
  Phone,
  Email,
  AccessTime,
  AttachMoney,
  Check,
  Close,
  Edit,
  Visibility,
  Today,
  Schedule,
  Cancel,
  Store,
} from '@mui/icons-material';
// Removed date picker imports to fix module resolution errors
import { appointmentsAPI, adminAPI } from '../../services/api';
import { useNotification } from '../../context/NotificationContext';

const statusColors = {
  pending: 'warning',
  confirmed: 'success',
  completed: 'info',
  cancelled: 'error',
  'no-show': 'default',
};

const Bookings = () => {
  const { showNotification } = useNotification();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch bookings from API
  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use admin API to get appointments with full details
      const response = await adminAPI.getAppointments();
      console.log('API Response:', response); // Debug log
      
      // Transform API response to match component structure
      // The API returns appointments array in response.appointments (not response.data)
      const appointmentsData = response.appointments || response.data || [];
      console.log('Appointments data:', appointmentsData); // Debug log
      
      const transformedBookings = appointmentsData.map(appointment => {
        console.log('Processing appointment:', appointment); // Debug log
        
        // Handle client name properly
        const clientName = appointment.client 
          ? `${appointment.client.firstName || ''} ${appointment.client.lastName || ''}`.trim() || appointment.client.name || 'Unknown Customer'
          : 'Unknown Customer';
        
        // Appointments belong to salons; provider is optional for older records.
        const providerName = appointment.provider 
          ? `${appointment.provider.firstName || ''} ${appointment.provider.lastName || ''}`.trim() || appointment.provider.businessName || appointment.provider.name || 'Unknown Provider'
          : 'Unknown Provider';
        const salonName = appointment.salon?.name || providerName;
        const salonAddress = appointment.salon?.address;
        const salonAddressText = salonAddress
          ? [salonAddress.street, salonAddress.city, salonAddress.state, salonAddress.zipCode, salonAddress.country]
            .filter(Boolean)
            .join(', ')
          : appointment.provider?.address || 'Address not available';
        
        return {
          id: appointment._id || appointment.id,
          customerName: clientName,
          customerEmail: appointment.client?.email || 'No email provided',
          customerPhone: appointment.client?.phone || 'No phone provided',
          salon: {
            name: salonName,
            address: salonAddressText,
          },
          service: appointment.service?.name || 'Unknown Service',
          date: appointment.appointmentDate ? new Date(appointment.appointmentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          time: appointment.startTime || appointment.appointmentTime || '00:00',
          duration: appointment.duration || 60,
          price: appointment.totalAmount || appointment.servicePrice || 0,
          status: appointment.status || 'pending',
          notes: appointment.clientNotes || appointment.notes || '',
          createdAt: appointment.createdAt || new Date().toISOString(),
          appointmentNumber: appointment.appointmentNumber || `APT-${appointment._id || appointment.id}`,
          // Keep original appointment data for API operations
          originalData: appointment
        };
      });
      
      console.log('Transformed bookings:', transformedBookings); // Debug log
      setBookings(transformedBookings);
      setFilteredBookings(transformedBookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError('Failed to load bookings. Please try again.');
      showNotification('Failed to load bookings', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [searchTerm, statusFilter, dateFilter, selectedTab, bookings]);

  const filterBookings = () => {
    let filtered = bookings;

    // Filter by tab (date-based)
    const today = new Date().toISOString().split('T')[0];
    switch (selectedTab) {
      case 0: // Today
        filtered = filtered.filter(booking => booking.date === today);
        break;
      case 1: // Upcoming
        filtered = filtered.filter(booking => booking.date > today);
        break;
      case 2: // Past
        filtered = filtered.filter(booking => booking.date < today);
        break;
      case 3: // All
      default:
        break;
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(booking =>
        booking.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.service.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Filter by date
    if (dateFilter) {
      const filterDate = dateFilter.toISOString().split('T')[0];
      filtered = filtered.filter(booking => booking.date === filterDate);
    }

    setFilteredBookings(filtered);
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    setLoading(true);
    try {
      // Find the booking to get original appointment data
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking) {
        throw new Error('Booking not found');
      }

      // Use appointmentsAPI to update the status
      let response;
      switch (newStatus) {
        case 'confirmed':
          response = await appointmentsAPI.confirm(bookingId);
          break;
        case 'cancelled':
          response = await appointmentsAPI.cancel(bookingId);
          break;
        case 'completed':
          response = await appointmentsAPI.complete(bookingId);
          break;
        default:
          // For other status changes, use the update method
          response = await appointmentsAPI.update(bookingId, { status: newStatus });
      }

      // Update local state with the new status
      const updatedBookings = bookings.map(booking =>
        booking.id === bookingId
          ? { ...booking, status: newStatus }
          : booking
      );
      setBookings(updatedBookings);
      
      showNotification(`Booking ${newStatus} successfully!`, 'success');
      
      // If we're viewing details of this booking, close the dialog
      if (selectedBooking && selectedBooking.id === bookingId) {
        setDetailsDialogOpen(false);
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      showNotification('Failed to update booking status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsDialogOpen(true);
  };

  const getBookingCounts = () => {
    const today = new Date().toISOString().split('T')[0];
    return {
      today: bookings.filter(b => b.date === today).length,
      upcoming: bookings.filter(b => b.date > today).length,
      past: bookings.filter(b => b.date < today).length,
      all: bookings.length,
    };
  };

  const getStatusCounts = () => {
    return {
      pending: bookings.filter(b => b.status === 'pending').length,
      confirmed: bookings.filter(b => b.status === 'confirmed').length,
      completed: bookings.filter(b => b.status === 'completed').length,
      cancelled: bookings.filter(b => b.status === 'cancelled').length,
    };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const counts = getBookingCounts();
  const statusCounts = getStatusCounts();

  return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            Bookings Management
          </Typography>
          <Button variant="contained" startIcon={<Event />}>
            View Calendar
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Loading bookings...
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {statusCounts.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Confirmed
                </Typography>
                <Typography variant="h4" color="success.main">
                  {statusCounts.confirmed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4" color="info.main">
                  {statusCounts.completed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h4">
                  ₹{bookings.filter(b => b.status === 'completed').reduce((sum, b) => sum + b.price, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="fullWidth"
          >
            <Tab
              label={
                <Badge badgeContent={counts.today} color="primary">
                  Today
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={counts.upcoming} color="primary">
                  Upcoming
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={counts.past} color="primary">
                  Past
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={counts.all} color="primary">
                  All
                </Badge>
              }
            />
          </Tabs>
        </Card>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search bookings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Filter by Date"
                  type="date"
                  value={dateFilter ? dateFilter.toISOString().split('T')[0] : ''}
                  onChange={(e) => setDateFilter(e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setDateFilter(null);
                  }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Bookings Table */}
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Salon</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {booking.customerName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {booking.customerEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Store fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {booking.salon.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {booking.salon.address}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {booking.service}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {formatDate(booking.date)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatTime(booking.time)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <AccessTime fontSize="small" sx={{ mr: 0.5 }} />
                          {booking.duration} min
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <span style={{ marginRight: '4px', fontSize: '14px' }}>₹</span>
                          {booking.price}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={booking.status}
                          color={statusColors[booking.status]}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleViewDetails(booking)}
                          color="primary"
                          size="small"
                        >
                          <Visibility />
                        </IconButton>
                        {booking.status === 'pending' && (
                          <>
                            <IconButton
                              onClick={() => handleStatusChange(booking.id, 'confirmed')}
                              color="success"
                              size="small"
                            >
                              <Check />
                            </IconButton>
                            <IconButton
                              onClick={() => handleStatusChange(booking.id, 'cancelled')}
                              color="error"
                              size="small"
                            >
                              <Close />
                            </IconButton>
                          </>
                        )}
                        {booking.status === 'confirmed' && (
                          <IconButton
                            onClick={() => handleStatusChange(booking.id, 'completed')}
                            color="info"
                            size="small"
                          >
                            <Check />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Booking Details Dialog */}
        <Dialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Booking Details
          </DialogTitle>
          <DialogContent>
            {selectedBooking && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Customer Information
                  </Typography>
                  <Box mb={2}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Person sx={{ mr: 1 }} />
                      <Typography>{selectedBooking.customerName}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Email sx={{ mr: 1 }} />
                      <Typography>{selectedBooking.customerEmail}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Phone sx={{ mr: 1 }} />
                      <Typography>{selectedBooking.customerPhone}</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Booking Information
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Service
                    </Typography>
                    <Typography variant="body1" mb={1}>
                      {selectedBooking.service}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date & Time
                    </Typography>
                    <Typography variant="body1" mb={1}>
                      {formatDate(selectedBooking.date)} at {formatTime(selectedBooking.time)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" mb={1}>
                      {selectedBooking.duration} minutes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Price
                    </Typography>
                    <Typography variant="body1" mb={1}>
                      ₹{selectedBooking.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedBooking.status}
                      color={statusColors[selectedBooking.status]}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                </Grid>
                {selectedBooking.notes && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedBooking.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
            {selectedBooking && selectedBooking.status === 'pending' && (
              <>
                <Button
                  onClick={() => {
                    handleStatusChange(selectedBooking.id, 'confirmed');
                    setDetailsDialogOpen(false);
                  }}
                  color="success"
                  variant="contained"
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => {
                    handleStatusChange(selectedBooking.id, 'cancelled');
                    setDetailsDialogOpen(false);
                  }}
                  color="error"
                  variant="outlined"
                >
                  Cancel
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default Bookings;
